import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

export interface ModelMetadata {
  meshCount: number;
  vertexCount: number;
  triangleCount: number;
  materialCount: number;
  textureCount: number;
  animationCount: number;
  animationNames: string[];
  dimensions: {
    width: number;
    height: number;
    depth: number;
  };
  hasAnimations: boolean;
  hasPBRMaterials: boolean;
}

interface ModelPreview3DProps {
  modelUrl: string;
  className?: string;
  onMetadataLoaded?: (metadata: ModelMetadata) => void;
}

export default function ModelPreview3D({ modelUrl, className = '', onMetadataLoaded }: ModelPreview3DProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<BABYLON.Engine | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Check if BabylonJS is available
    const BABYLON = (window as unknown as { BABYLON?: typeof import('babylonjs') }).BABYLON;
    if (!BABYLON) {
      setError('3D viewer not available');
      setIsLoading(false);
      return;
    }

    const canvas = canvasRef.current;
    let engine: BABYLON.Engine | null = null;
    let scene: BABYLON.Scene | null = null;

    const init = async () => {
      try {
        console.log('[ModelPreview3D] Loading model from:', modelUrl);

        // Create engine (no alpha for opaque rendering)
        engine = new BABYLON.Engine(canvas, true, {
          preserveDrawingBuffer: true,
          stencil: true,
          alpha: false,
        });
        engineRef.current = engine;

        // Create scene with dark background (fully opaque)
        scene = new BABYLON.Scene(engine);
        scene.clearColor = new BABYLON.Color4(0.06, 0.06, 0.10, 1);

        // Camera - ArcRotate for orbiting (rotation only, no zoom)
        // alpha = camera horizontal angle (0 = +Z, PI/2 = +X, PI = -Z, 3PI/2 = -X)
        // Start at ~1.52PI to view model from front
        const camera = new BABYLON.ArcRotateCamera(
          'camera',
          Math.PI * 1.52,
          Math.PI / 3,
          4,
          BABYLON.Vector3.Zero(),
          scene
        );
        camera.attachControl(canvas, true);

        // Disable zoom and pan - only rotation allowed
        camera.lowerRadiusLimit = 4;
        camera.upperRadiusLimit = 4;
        camera.wheelPrecision = 99999; // Effectively disable wheel zoom
        camera.pinchPrecision = 99999; // Effectively disable pinch zoom
        camera.panningSensibility = 0; // Disable panning completely

        // Lights
        const hemiLight = new BABYLON.HemisphericLight(
          'hemi',
          new BABYLON.Vector3(0, 1, 0),
          scene
        );
        hemiLight.intensity = 0.6;

        const dirLight = new BABYLON.DirectionalLight(
          'dir',
          new BABYLON.Vector3(-0.5, -1, 0.5),
          scene
        );
        dirLight.intensity = 0.8;

        // Simple environment for PBR (skip heavy environment texture)
        scene.ambientColor = new BABYLON.Color3(0.3, 0.3, 0.3);

        // Start render loop immediately so we see the scene
        engine.runRenderLoop(() => {
          scene?.render();
        });

        // Handle resize
        const handleResize = () => engine?.resize();
        window.addEventListener('resize', handleResize);

        // Load model - split URL into root and filename for BabylonJS
        const lastSlash = modelUrl.lastIndexOf('/');
        const rootUrl = modelUrl.substring(0, lastSlash + 1);
        const fileName = modelUrl.substring(lastSlash + 1);

        console.log('[ModelPreview3D] Root URL:', rootUrl);
        console.log('[ModelPreview3D] Filename:', fileName);

        const result = await BABYLON.SceneLoader.ImportMeshAsync('', rootUrl, fileName, scene);
        console.log('[ModelPreview3D] Model loaded, meshes:', result.meshes.length);

        if (result.meshes.length > 0) {
          const root = result.meshes[0];

          // Calculate bounding box and center model
          let min = new BABYLON.Vector3(Infinity, Infinity, Infinity);
          let max = new BABYLON.Vector3(-Infinity, -Infinity, -Infinity);

          // Extract metadata
          let totalVertices = 0;
          let totalTriangles = 0;
          const materials = new Set<string>();
          const textures = new Set<string>();
          let hasPBR = false;

          result.meshes.forEach((mesh) => {
            if (mesh.getBoundingInfo) {
              const boundingInfo = mesh.getBoundingInfo();
              min = BABYLON.Vector3.Minimize(min, boundingInfo.boundingBox.minimumWorld);
              max = BABYLON.Vector3.Maximize(max, boundingInfo.boundingBox.maximumWorld);
            }

            // Count vertices and triangles
            if (mesh.getTotalVertices) {
              totalVertices += mesh.getTotalVertices();
            }
            if (mesh.getTotalIndices) {
              totalTriangles += Math.floor(mesh.getTotalIndices() / 3);
            }

            // Collect materials and textures
            if (mesh.material) {
              materials.add(mesh.material.name || mesh.material.id);

              const pbr = mesh.material as BABYLON.PBRMaterial;
              if (pbr.albedoTexture || pbr.metallicTexture || pbr.bumpTexture) {
                hasPBR = true;
              }
              if (pbr.albedoTexture) textures.add('albedo');
              if (pbr.metallicTexture) textures.add('metallic');
              if (pbr.bumpTexture) textures.add('normal');
              if (pbr.ambientTexture) textures.add('ambient');
              if (pbr.emissiveTexture) textures.add('emissive');
            }
          });

          const center = min.add(max).scale(0.5);
          const size = max.subtract(min);
          const maxDim = Math.max(size.x, size.y, size.z);

          // Extract animation info
          const animationNames = result.animationGroups?.map(ag => ag.name) || [];

          // Build metadata object
          const metadata: ModelMetadata = {
            meshCount: result.meshes.length - 1, // Exclude root
            vertexCount: totalVertices,
            triangleCount: totalTriangles,
            materialCount: materials.size,
            textureCount: textures.size,
            animationCount: result.animationGroups?.length || 0,
            animationNames,
            dimensions: {
              width: parseFloat(size.x.toFixed(2)),
              height: parseFloat(size.y.toFixed(2)),
              depth: parseFloat(size.z.toFixed(2)),
            },
            hasAnimations: (result.animationGroups?.length || 0) > 0,
            hasPBRMaterials: hasPBR,
          };

          console.log('[ModelPreview3D] Metadata:', metadata);
          onMetadataLoaded?.(metadata);

          // Scale to fit - smaller for better framing
          const targetSize = 2.7;
          const scaleFactor = targetSize / maxDim;
          root.scaling = new BABYLON.Vector3(scaleFactor, scaleFactor, scaleFactor);

          // Center the model
          root.position = center.scale(-scaleFactor);

          // Ensure materials are fully opaque
          result.meshes.forEach((mesh) => {
            if (mesh.material) {
              mesh.material.alpha = 1;
              mesh.material.backFaceCulling = true;
              // For PBR materials, disable alpha on textures
              const pbr = mesh.material as BABYLON.PBRMaterial;
              if (pbr.albedoTexture) {
                pbr.albedoTexture.hasAlpha = false;
              }
              if (pbr.transparencyMode !== undefined) {
                pbr.transparencyMode = BABYLON.PBRMaterial.PBRMATERIAL_OPAQUE;
              }
            }
          });

          // Adjust camera - closer for better view
          camera.radius = targetSize * 1.2;
          camera.lowerRadiusLimit = camera.radius;
          camera.upperRadiusLimit = camera.radius;
          camera.target = BABYLON.Vector3.Zero();

          // Play first animation if exists
          if (result.animationGroups && result.animationGroups.length > 0) {
            console.log('[ModelPreview3D] Found animations:', result.animationGroups.length);
            const firstAnimation = result.animationGroups[0];
            firstAnimation.start(true); // Loop the animation
          }
        }

        setIsLoading(false);
      } catch (err) {
        console.error('[ModelPreview3D] Error:', err);
        setError(`Failed to load: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setIsLoading(false);
      }
    };

    init();

    return () => {
      if (engineRef.current) {
        engineRef.current.dispose();
        engineRef.current = null;
      }
    };
  }, [modelUrl, onMetadataLoaded]);

  return (
    <div className={`relative ${className}`}>
      <canvas
        ref={canvasRef}
        className="w-full h-full rounded-2xl"
        style={{ touchAction: 'none' }}
      />

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl">
          <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl">
          <p className="text-white/50 text-sm">{error}</p>
        </div>
      )}

      {/* Interaction hint */}
      {!isLoading && !error && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] text-white/40 bg-black/30 px-2 py-1 rounded-full">
          Drag to rotate
        </div>
      )}
    </div>
  );
}
